export type PronunciationCount = {
  pronunciation: string
  count: number
}

export type WordState = {
  word: string
  pronunciations: PronunciationCount[]
  registered: boolean
}

export type RegisterResponse = {
  word: string
  pronunciations: PronunciationCount[]
  voted: string
}

export class ApiError extends Error {
  readonly status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

export function wordPath(word: string): string {
  return `/${encodeURIComponent(word)}`
}

function parsePronunciations(json: string | undefined): PronunciationCount[] {
  if (!json) return []
  try {
    const parsed: unknown = JSON.parse(json)
    return Array.isArray(parsed) ? (parsed as PronunciationCount[]) : []
  } catch {
    return []
  }
}

/** 単語ページの data-* から状態を読む。要素は 404 のページにも含まれる */
export function readWordState(element: HTMLElement): WordState {
  return {
    word: element.dataset.word ?? '',
    pronunciations: parsePronunciations(element.dataset.pronunciations),
    registered: element.dataset.registered === '1',
  }
}

/**
 * 単語ページのHTMLを取得して状態だけを取り出す。
 * エッジキャッシュ済みのページをそのまま使えるため、専用のAPIを増やさない。
 */
export async function fetchWordState(word: string): Promise<WordState> {
  const res = await fetch(wordPath(word), { headers: { Accept: 'text/html' } })
  const html = await res.text()
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const element = doc.getElementById('word-app')
  if (!element) throw new ApiError(res.status, 'ページを取得できませんでした')
  return readWordState(element)
}

export async function searchWords(q: string): Promise<string[]> {
  const res = await fetch(`/api/search?q=${encodeURIComponent(q)}`, {
    headers: { Accept: 'application/json' },
  })
  const data = (await res.json()) as { words?: string[]; error?: { message?: string } }
  if (!res.ok) throw new ApiError(res.status, data.error?.message ?? '検索に失敗しました')
  return data.words ?? []
}

export type RegisterInput = {
  word: string
  pronunciation: string
  voterId: string
  turnstileToken: string
}

/** POST / (登録と投票) */
export async function register(input: RegisterInput): Promise<RegisterResponse> {
  const body = new URLSearchParams({
    word: input.word,
    pronunciation: input.pronunciation,
    voter_id: input.voterId,
    'cf-turnstile-response': input.turnstileToken,
  })
  const res = await fetch('/', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
    },
    body,
  })
  const data = (await res.json()) as RegisterResponse & { error?: { message?: string } }
  if (!res.ok) throw new ApiError(res.status, data.error?.message ?? '登録に失敗しました')
  return data
}
